import React from 'react';

import styled from '@mint-lib/styled';
import { CheckboxProps as CheckboxPropsM } from '@mui/material/Checkbox';
import ListItemButton, {
  ListItemButtonProps as ListItemButtonPropsM,
} from '@mui/material/ListItemButton';
import { RadioProps as RadioPropsM } from '@mui/material/Radio';

import Box from '../Box/Box.jsx';
import Checkbox from '../Checkbox/Checkbox.jsx';
import Radio from '../Radio/Radio.jsx';
import Truncation from '../Truncation/Truncation.jsx';
import Typography from '../Typography/Typography.jsx';

export interface PopoverItemProps extends ListItemButtonPropsM {
  /**
   * Text inside menu item
   */
  text: string;
  /**
   * Variant of the text layout
   * @default multiline
   */
  textLayout?: 'inline' | 'multiline';
  /**
   * cb for click on menu item
   */
  onItemClick?: (...args: any) => void;
  variant?: 'checkbox' | 'radio' | 'default';
  CheckboxProps?: CheckboxPropsM;
  RadioProps?: RadioPropsM;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  size?: 'large' | 'medium';
  disabled?: boolean;
}

/**
 * @deprecated Use the new PopoverItem in itemVariants from @mint-lib/tw-components/lib/variants.tsx instead.
 */
const PopoverItem: React.FC<PopoverItemProps> = ({
  text,
  onItemClick,
  CheckboxProps,
  RadioProps,
  variant = 'default',
  endAdornment,
  startAdornment,
  textLayout = 'inline',
  size = 'medium',
  ...props
}) => {
  const inputVariants = {
    radio: () => (
      <Radio
        {...RadioProps}
        checked={props.selected}
        disabled={props.disabled}
      />
    ),
    checkbox: () => (
      <Checkbox
        {...CheckboxProps}
        checked={props.selected}
        disabled={props.disabled}
      />
    ),
    default: () => null,
  };
  return (
    <StyledListItemButton
      onClick={(e) => onItemClick && onItemClick(e)}
      size={size}
      {...props}
    >
      {variant !== 'default' ? (
        <Box sx={{ height: '20px' }}>{inputVariants[variant]()}</Box>
      ) : null}
      {startAdornment}
      {textLayout === 'inline' ? (
        <Truncation
          tooltipTitle={text}
          variant={
            props.selected || props.disabled ? 'bodyShort02' : 'bodyShort01'
          }
        >
          {text}
        </Truncation>
      ) : (
        <Typography
          variant={
            props.selected || props.disabled ? 'bodyShort02' : 'bodyShort01'
          }
        >
          {text}
        </Typography>
      )}
      {endAdornment}
    </StyledListItemButton>
  );
};

export default PopoverItem;

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{
  size?: 'medium' | 'large';
}>(({ size, theme }) => ({
  boxSizing: 'border-box',
  backgroundColor: theme.palette.ui.primary,
  color: theme.palette.text.primary,
  minHeight: size === 'medium' ? '2rem' : '2.5rem',
  width: size === 'medium' ? '10rem' : '13rem',
  gap: '8px',
  padding: '0px 12px',
  '&:hover': {
    backgroundColor: theme.palette.ui.secondary,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.background.tertiary,
    '.MuiSvgIcon-root': {
      color: theme.palette.tertiary.dark,
    },
    color: theme.palette.tertiary.dark,
    '&:hover': {
      backgroundColor: theme.palette.ui.highlight,
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.ui.hoverSelected,
    },
  },
  '&.Mui-disabled': {
    opacity: 1,
    backgroundColor: theme.palette.ui.primary,
    color: theme.palette.text.disabled,
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.ui.secondary,
    color: theme.palette.tertiary.dark,
  },
}));
